import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';
import media from '../style/media';
import { Heart, Info, Settings as SettingsIcon, Scan, ShieldCheck, Clock, ChevronRight } from 'lucide-react';
import { useGetAllVitalsQuery } from '../services/vitalsApi';
import { StorageKeys } from '../utils/storage';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard, Grid } from '../style/layout';

const WellnessCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 28px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  ${media.tablet`
    flex-direction: row;
    align-items: center;
    padding: 2.5rem;
  `}
`;

const WellnessInfo = styled.div`
  flex: 1;
`;

const WellnessTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WellnessStatus = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 1rem;
`;

const WellnessMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
  
  svg {
    color: #94a3b8;
  }
`;

const GaugeContainer = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${media.tablet`
    width: 160px;
    height: 160px;
  `}
`;

const Pulse = styled.div<{ hue: number }>`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: hsla(${p => p.hue}, 85%, 55%, 0.1);
  animation: pulse 3s ease-out infinite;
  
  @keyframes pulse {
    0% { transform: scale(0.8); opacity: 0.5; }
    70% { transform: scale(1.2); opacity: 0; }
    100% { transform: scale(0.8); opacity: 0; }
  }
`;

const GaugeValue = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ScoreValue = styled.span`
  font-size: 2.5rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
`;

const ScoreMax = styled.span`
  font-size: 0.875rem;
  color: #94a3b8;
  font-weight: 600;
  margin-top: 0.25rem;
`;

const MenuButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.75rem;
  background: white;
  color: #0f172a;
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 160px;
  width: 100%;
  text-align: left;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: #e2e8f0;
    
    .icon-wrapper {
      background: #f1f5f9;
      transform: scale(1.1);
    }
  }

  &:active {
    transform: translateY(-4px);
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;
    color: #3b82f6;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .label {
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  .description {
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 400;
  }
`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const logout = useLogout();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const cameras = useCameras();

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  const pid = useMemo(() => localStorage.getItem(StorageKeys.PATIENT_ID) || '', []);
  const isPidValid = pid && pid !== 'null';
  const orgId = useMemo(() => localStorage.getItem(StorageKeys.ORG_ID) || '', []);
  const isOrgIdValid = orgId && orgId !== 'null';

  const user = useMemo(() => {
    const userStr = localStorage.getItem(StorageKeys.AUTH_USER);
    return userStr ? JSON.parse(userStr) : null;
  }, []);
  const isOrgType3 = user?.org_type === 3;

  const { data: vitalsData } = useGetAllVitalsQuery(
    { 
      pageNo: 1, 
      pagesize: 1, 
      pagination_required: true, 
      ...((isOrgType3 && isOrgIdValid) ? { org_id: orgId } : (isPidValid ? { patient_id: pid } : {}))
    }
  );

  const latest = vitalsData?.vitals?.[0];
  const wellnessScore: number = (() => {
    try {
      const raw = latest?.WELLNESS_INDEX;
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      const val = Number(parsed?.value);
      if (!isFinite(val)) return 0;
      return Math.max(0, Math.min(10, val));
    } catch { return 0; }
  })();

  const recorded = (() => {
    try {
      const rd = latest?.recorded_date as string | undefined;
      if (!rd) return '-';
      const d = new Date(rd);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      const h24 = d.getHours();
      const min = String(d.getMinutes()).padStart(2, '0');
      const period = h24 >= 12 ? 'PM' : 'AM';
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
      return `${mm}/${dd}/${yyyy} ${h12}:${min} ${period}`;
    } catch { return '-'; }
  })();

  const getWellnessLabel = (score: number) => {
    if (score === 0) return 'No Data';
    if (score >= 8.5) return 'Excellent';
    if (score >= 7.0) return 'Very Good';
    if (score >= 5.5) return 'Good';
    if (score >= 4.0) return 'Fair';
    if (score >= 2.5) return 'Poor';
    return 'Critical';
  };

  const target = Math.round((wellnessScore / 10) * 100);
  const hue = 0 + (wellnessScore / 10) * 120;
  const [progress, setProgress] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    setProgress(0);
    setDisplayScore(0);
    const t = setTimeout(() => setProgress(target), 50);

    const steps = 20;
    let i = 0;
    const inc = wellnessScore / steps;
    const timer = setInterval(() => {
      i += 1;
      setDisplayScore(prev => i >= steps ? wellnessScore : prev + inc);
      if (i >= steps) clearInterval(timer);
    }, 30);

    return () => { clearTimeout(t); clearInterval(timer); };
  }, [target, wellnessScore]);

  useEffect(() => {
    updateLicenseStatus(true);
  }, [updateLicenseStatus]);

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
      <ContentWrapper>
        <GlassCard>
          <Header>
            <Title>Dashboard</Title>
            <Subtitle>Predict, promote, and prevent your wellness</Subtitle>
          </Header>

          <WellnessCard>
            <WellnessInfo>
              <WellnessTitle>Wellness Score</WellnessTitle>
              <WellnessStatus>{getWellnessLabel(wellnessScore)}</WellnessStatus>
              <WellnessMeta>
                {/* <MetaItem>
                  <Info size={16} />
                  <span>Based on your latest biometric scan</span>
                </MetaItem> */}
                <MetaItem>
                  <Clock size={16} />
                  <span>Last scan: {recorded}</span>
                </MetaItem>
              </WellnessMeta>
            </WellnessInfo>

            <GaugeContainer>
              <svg width="100%" height="100%" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" stroke="#f1f5f9" strokeWidth="10" fill="none" />
                <circle
                  cx="60" cy="60" r="54"
                  stroke={`hsl(${hue}, 85%, 55%)`}
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 54}
                  strokeDashoffset={(1 - progress / 100) * (2 * Math.PI * 54)}
                  style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <Pulse hue={hue} />
              <GaugeValue>
                <ScoreValue>{displayScore.toFixed(1)}</ScoreValue>
                <ScoreMax>/ 10.0</ScoreMax>
              </GaugeValue>
            </GaugeContainer>
          </WellnessCard>

          <Grid>

            {isPidValid && (
              <MenuButton onClick={() => navigate('/scan')}>
                <div className="icon-wrapper"><Scan /></div>
                <div style={{ width: '100%' }}>
                  <div className="label-row">
                    <span className="label">Start Scan</span>
                    <ChevronRight size={18} color="#cbd5e1" />
                  </div>
                  <p className="description">Begin a new health assessment</p>
                </div>
              </MenuButton>
            )}

            {(isOrgIdValid && isOrgType3) && (
              <MenuButton onClick={() => navigate('/fullScan')}>
                <div className="icon-wrapper"><Scan /></div>
                <div style={{ width: '100%' }}>
                  <div className="label-row">
                    <span className="label">Full Scan</span>
                    <ChevronRight size={18} color="#cbd5e1" />
                  </div>
                  <p className="description">Enter user details for a comprehensive scan</p>
                </div>
              </MenuButton>
            )}

            {(isPidValid || (isOrgIdValid && isOrgType3)) && (
              <>
                <MenuButton onClick={() => navigate('/vitals')}>
                  <div className="icon-wrapper" style={{ color: '#ec4899' }}><Heart /></div>
                  <div style={{ width: '100%' }}>
                    <div className="label-row">
                      <span className="label">{isOrgIdValid && isOrgType3 ? 'Vitals' : 'My Vitals'}</span>
                      <ChevronRight size={18} color="#cbd5e1" />
                    </div>
                    <p className="description">Track health history and trends</p>
                  </div>
                </MenuButton>

                <MenuButton onClick={() => navigate('/licenseInfo')}>
                  <div className="icon-wrapper" style={{ color: '#10b981' }}><ShieldCheck /></div>
                  <div style={{ width: '100%' }}>
                    <div className="label-row">
                      <span className="label">License Info</span>
                      <ChevronRight size={18} color="#cbd5e1" />
                    </div>
                    <p className="description">View your license details and scan credits.</p>
                  </div>
                </MenuButton>
              </>
            )}

            



            <MenuButton onClick={() => navigate('/settings')}>
              <div className="icon-wrapper" style={{ color: '#64748b' }}><SettingsIcon /></div>
              <div style={{ width: '100%' }}>
                <div className="label-row">
                  <span className="label">Settings</span>
                  <ChevronRight size={18} color="#cbd5e1" />
                </div>
                <p className="description">Configure your profile and preferences</p>
              </div>
            </MenuButton>
          </Grid>
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Dashboard;
